import { userQueryKeys } from '@/config/queryKeys';
import { useAgGridDatasourceRefresh } from '@/hooks';
import { getDateAsString } from '@/lib';
import { createAgGridDatasource } from '@/lib/createAgGridDatasource';
import { PaginatedUsersResponse, UserPublic } from '@/types';
import { Avatar } from '@nautilusbio/nb-design-system/Avatar';
import Badge from '@nautilusbio/nb-design-system/Badge';
import { getTextClassName } from '@nautilusbio/nb-design-system/helpers';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { ColDef } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import React, { FC, RefObject, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import tableStyles from '../../styles/components/table.module.css';
import globalStyles from '../../styles/index.module.css';

import styles from './styles.module.css';
import SuperAdminToggleCell from './SuperAdminToggleCell';

const localStorageKey = 'super-admin-users-agGridSortState';

export type SuperAdminGlobalTableProps = {
  gridRef: RefObject<AgGridReact<UserPublic> | null>;
  currentUserId: string;
  orgId: string;
};

const tableContainerClasses = { className: globalStyles.scrollbars };

const SuperAdminGlobalTable: FC<SuperAdminGlobalTableProps> = ({ gridRef, currentUserId, orgId }) => {
  // Invalidates the TanStack Query datasource cache for every cached page/sort/filter
  // combination, then purges AG Grid's block cache and triggers a full reload.
  // Using refreshServerSide({ purge: false }) alone returns stale data because
  // createAgGridDatasource caches responses with a 1-day staleTime — invalidation
  // must happen first so getRows fetches fresh data from the server.
  const handleMutationSettled = useAgGridDatasourceRefresh(userQueryKeys.endpoints.globalSearch(), gridRef);

  const { t: tUsers } = useTranslation('users');
  const { t: tGeneral } = useTranslation('general');

  const colDefs = useMemo<ColDef<UserPublic>[]>(
    () => [
      // full_name with avatar
      {
        cellRenderer: ({ data }: { data: UserPublic }) => (
          <Stack alignItems="center" gap={4} orientation="horizontal">
            <Avatar size="xs2">{data.full_name}</Avatar>
            <Text as="span" className={getTextClassName('sm')}>
              {data.full_name}
            </Text>
          </Stack>
        ),
        field: 'full_name',
        headerName: tGeneral('name'),
        sortable: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains', 'equals', 'startsWith', 'endsWith', 'blank', 'notBlank'],
          suppressAndOrCondition: true,
          buttons: ['reset', 'apply'],
        },
        minWidth: 200,
      },

      // email
      {
        field: 'email',
        headerName: tGeneral('email'),
        sortable: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains', 'equals', 'startsWith', 'endsWith', 'blank', 'notBlank'],
          suppressAndOrCondition: true,
          buttons: ['reset', 'apply'],
        },
        minWidth: 220,
      },

      // is_super_admin (platform-level badge)
      {
        cellClass: tableStyles.tableCellCenter,
        cellRenderer: ({ value }: { value: boolean }) => (
          <Badge className={styles.badge} size="xs" variant={value ? 'success' : 'default'}>
            {value ? tUsers('is_super_admin_yes') : tUsers('is_super_admin_no')}
          </Badge>
        ),
        field: 'is_super_admin',
        headerName: tUsers('is_super_admin_column_header'),
        sortable: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [true, false],
          valueFormatter: ({ value }: { value: boolean }) =>
            value ? tUsers('is_super_admin_yes') : tUsers('is_super_admin_no'),
          buttons: ['reset', 'apply'],
        },
        maxWidth: 180,
      },

      // SuperAdmin org-toggle column
      {
        cellClass: tableStyles.tableCellCenter,
        cellRenderer: ({ data }: { data: UserPublic }) => (
          <SuperAdminToggleCell
            currentUserId={currentUserId}
            isSuperAdminOverride={data.is_super_admin}
            onMutationSettled={handleMutationSettled}
            orgId={orgId}
            user={data}
          />
        ),
        colId: 'super_admin_toggle',
        field: 'is_super_admin' as const,
        filter: false,
        width: 80,
        headerName: tUsers('super_admin_column_header'),
        sortable: false,
      },

      // created_at
      {
        field: 'created_at',
        headerName: tGeneral('created_at'),
        sortable: true,
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: ['equals', 'notEqual', 'lessThan', 'greaterThan', 'inRange', 'blank', 'notBlank'],
          suppressAndOrCondition: false,
          buttons: ['reset', 'apply'],
        },
        valueFormatter: ({ value }) => (value ? getDateAsString(new Date(value)) : '—'),
        maxWidth: 210,
      },

      // updated_at (hidden by default)
      {
        field: 'updated_at',
        headerName: tUsers('updated_at_column_header'),
        sortable: true,
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: ['equals', 'notEqual', 'lessThan', 'greaterThan', 'inRange', 'blank', 'notBlank'],
          suppressAndOrCondition: false,
          buttons: ['reset', 'apply'],
        },
        hide: true,
        valueFormatter: ({ value }) => (value ? getDateAsString(new Date(value)) : '—'),
        maxWidth: 210,
      },
    ],
    [tGeneral, tUsers, currentUserId, orgId, handleMutationSettled],
  );

  // AG Grid server-side datasource via POST /users/search
  // is_super_admin is a boolean field — AG Grid's Set Filter serialises filter
  // model values as strings, so we declare it here to coerce "true"/"false"
  // back to actual booleans before the payload is sent to the API.
  const datasource = useMemo(
    () =>
      createAgGridDatasource<UserPublic, PaginatedUsersResponse>(userQueryKeys.endpoints.globalSearch(), undefined, {
        booleanFields: ['is_super_admin'],
      }),
    [],
  );

  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.setGridOption('serverSideDatasource', datasource);
    }
  }, [datasource, gridRef]);

  const handleGridReady = useCallback(
    (e: { api: AgGridReact<UserPublic>['api'] }) => {
      const savedColumnStateRaw = localStorage.getItem(localStorageKey);
      let savedColumnState = null;

      if (savedColumnStateRaw) {
        try {
          savedColumnState = JSON.parse(savedColumnStateRaw);
        } catch (err) {
          console.error('Failed to parse saved column state for global users table:', err);
          savedColumnState = null;
        }
      }

      if (savedColumnState) {
        e.api.applyColumnState({ state: savedColumnState });
      }

      e.api.setGridOption('serverSideDatasource', datasource);
    },
    [datasource],
  );

  const handleSortChanged = useCallback((e: { api: AgGridReact<UserPublic>['api'] }) => {
    const columnState = e.api.getColumnState();
    localStorage.setItem(localStorageKey, JSON.stringify(columnState));
  }, []);

  return (
    <Table
      columnDefs={colDefs}
      container={tableContainerClasses}
      domLayout="normal"
      enableResponsiveResize={true}
      onGridReady={handleGridReady}
      onSortChanged={handleSortChanged}
      pagination
      paginationAutoPageSize
      ref={gridRef}
      resizeDebounceMs={150}
      rowModelType="serverSide"
      tableId="super-admin-users-table"
      zebraStriping
    />
  );
};

export default SuperAdminGlobalTable;
